import React from "react";
import Head from "next/head";
import { useTranslations } from "next-intl";
import Page from "@components/Layout/page";
import { WEBPAGE_TITLE } from "@utils/Constants";
import BOARD_MEMBERS from "@utils/BoardMembers";
import BOARD_MEMBERS2 from "@utils/BoardMembers2";
import { Leaf } from "@components/Leaf/Leaf";
import Image from "next/legacy/image";
import { useInView } from "@hooks/useInView";
import styles from "./AboutUsPage.module.scss";

export default function AboutUsPage() {
    const t = useTranslations("AboutUsPage");

    const [heroRef, heroInView] = useInView();
    const [missionRef, missionInView] = useInView();
    const [valuesRef, valuesInView] = useInView();
    const [teamRef, teamInView] = useInView();
    const [team2Ref, team2InView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">{"Um okkur - " + WEBPAGE_TITLE}</title>
                <meta name="keywords" content="Um okkur" />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Um okkur`}
                    key="og:title"
                />
                <meta
                    name="og:description"
                    content="Markmið Laufsins er að gera sjálfbæra þróun aðgengilegri"
                    key="og:description"
                />
                <meta
                    name="description"
                    content="Markmið Laufsins er að gera sjálfbæra þróun aðgengilegri"
                />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />
                <Leaf className={styles.leafBg3} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>Um Laufið</span>
                        <h1 className={styles.heroTitle}>{t("header")}</h1>
                    </div>
                </section>

                <section
                    ref={missionRef}
                    className={`${styles.mission} ${styles.revealTarget} ${missionInView ? styles.revealed : ""}`}
                >
                    <div className={styles.missionCard}>
                        <div className={styles.missionIcon}>
                            <svg
                                width="28"
                                height="28"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                        </div>
                        <p className={styles.missionText}>
                            {t("within_laufinn")}
                        </p>
                    </div>
                    <div className={styles.missionCard}>
                        <div className={styles.missionIcon}>
                            <svg
                                width="28"
                                height="28"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 6v6l4 2" />
                            </svg>
                        </div>
                        <p className={styles.missionText}>
                            {t("our_goal_make_sustainable_development")}
                        </p>
                    </div>
                    <div className={styles.missionCard}>
                        <div className={styles.missionIcon}>
                            <svg
                                width="28"
                                height="28"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M18 20V10" />
                                <path d="M12 20V4" />
                                <path d="M6 20v-6" />
                            </svg>
                        </div>
                        <p className={styles.missionText}>
                            {t("software_went_live_november_2022")}
                        </p>
                    </div>
                    <div className={styles.missionCard}>
                        <div className={styles.missionIcon}>
                            <svg
                                width="28"
                                height="28"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                        </div>
                        <p className={styles.missionText}>
                            {t(
                                "addition_we_have_been_successful_collaboration",
                            )}
                        </p>
                    </div>
                </section>

                <section
                    ref={valuesRef}
                    className={`${styles.valuesSection} ${styles.revealTarget} ${valuesInView ? styles.revealed : ""}`}
                >
                    <div className={styles.valuesContent}>
                        <h2 className={styles.valuesTitle}>
                            {t("leaf_team_is_committed_to_bridging")}
                        </h2>
                    </div>
                </section>

                <section
                    ref={teamRef}
                    className={`${styles.teamSection} ${styles.revealTarget} ${teamInView ? styles.revealed : ""}`}
                >
                    <h2 className={styles.sectionTitle}>{t("our_board")}</h2>
                    <div className={styles.teamGrid}>
                        {BOARD_MEMBERS.map((member) => (
                            <TeamMember key={member.name} {...member} />
                        ))}
                    </div>
                </section>

                <section
                    ref={team2Ref}
                    className={`${styles.teamSection2} ${styles.revealTarget} ${team2InView ? styles.revealed : ""}`}
                >
                    <h2 className={styles.sectionTitle}>
                        {t("guesswork_operations_help")}
                    </h2>
                    <div className={styles.teamGrid}>
                        {BOARD_MEMBERS2.map((member) => (
                            <TeamMember key={member.name} {...member} />
                        ))}
                    </div>
                </section>
            </div>
        </Page>
    );
}

function TeamMember({ image, name, position, linkedInLink }) {
    return (
        <div className={styles.memberCard}>
            <div className={styles.memberImageWrapper}>
                <Image src={image} alt={name} layout="responsive" />
            </div>
            <h3 className={styles.memberName}>{name}</h3>
            {linkedInLink ? (
                <a
                    href={linkedInLink}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.memberPosition}
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                    >
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    {position}
                </a>
            ) : (
                <p className={styles.memberPosition}>{position}</p>
            )}
        </div>
    );
}
